import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import StatCard from '../../common/StatCard';
import BotToggle from '../../common/BotToggle';
import { showError } from '../../../utils/sweetAlertHelper';
import './OverviewDashboard.css';

const API_BASE_URL = import.meta.env.VITE_API_URL ;

/**
 * OverviewDashboard - Dashboard principal con resumen de todos los bots
 */
export default function OverviewDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [botsStatus, setBotsStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchDashboardData();
    
    // Actualizar cada 30 segundos
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('app_token');
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` }
      };
      
      // Obtener estado de todos los bots
      const statusRes = await axios.get(`${API_BASE_URL}/bot/config/status`, config);
      setBotsStatus(statusRes.data.data);
      
      // TODO: Implementar endpoint de summary global
      // const summaryRes = await axios.get('/api/dashboard/summary', config);
      // setSummary(summaryRes.data.data);
      
      // Temporal: datos de ejemplo
      setSummary({
        totalEquityUsd: 0,
        totalPnl: 0,
        gridBot: { equity: 0, pnl: 0, totalTrades: 0 },
        sniper: { equity: 0, pnl: 0, totalTrades: 0 },
        futures: { equity: 0, pnl: 0, totalTrades: 0 }
      });
      
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleBot = async (botType, symbol = null) => {
    try {
      const token = localStorage.getItem('app_token');
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` }
      };
      
      let endpoint = '';
      let currentStatus = false;
      
      if (botType === 'grid') {
        endpoint = `${API_BASE_URL}/bot/config/grid/${symbol}/toggle`;
        currentStatus = botsStatus.gridBot.enabled;
      } else if (botType === 'sniper') {
        endpoint = `${API_BASE_URL}/bot/config/sniper/toggle`;
        currentStatus = botsStatus.sniperBot.enabled;
      } else if (botType === 'futures') {
        endpoint = `${API_BASE_URL}/bot/config/futures/toggle`;
        currentStatus = botsStatus.futuresBot.enabled;
      }
      
      await axios.post(endpoint, { enabled: !currentStatus }, config);
      
      // Refrescar datos
      await fetchDashboardData();
    } catch (err) {
      await showError(err.response?.data?.error || err.message, `Error al Alternar Bot ${botType}`);
    }
  };
  
  if (loading) {
    return (
      <div className="overview-dashboard loading">
        <div className="spinner-large"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="overview-dashboard error">
        <h2>Error loading dashboard</h2>
        <p>{error}</p>
        <button onClick={fetchDashboardData}>Retry</button>
      </div>
    );
  }
  
  return (
    <div className="overview-dashboard">
      {/* Header con equity total */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Trading Dashboard</h1>
          <p className="header-subtitle">Overview of all your trading bots</p>
        </div>
        
        <div className="header-stats">
          <div className="total-equity">
            <span className="equity-label">Total Portfolio</span>
            <h2 className="equity-value">
              ${summary?.totalEquityUsd?.toLocaleString() || '0.00'}
            </h2>
            <span className={`equity-pnl ${summary?.totalPnl >= 0 ? 'profit' : 'loss'}`}>
              {summary?.totalPnl >= 0 ? '+' : ''}{summary?.totalPnl?.toFixed(2) || '0.00'}%
            </span>
          </div>
        </div>
      </div>
      
      {/* Bot Controls */}
      <div className="bot-controls-section">
        <h3>Bot Status & Controls</h3>
        <div className="bot-controls-grid">
          <BotToggle
            botName="Grid Bot (BTC-USDT)"
            enabled={botsStatus?.gridBot?.enabled}
            onToggle={() => toggleBot('grid', 'BTCUSDT')}
          />
          
          <BotToggle
            botName="Solana Sniper"
            enabled={botsStatus?.sniperBot?.enabled}
            onToggle={() => toggleBot('sniper')}
          />
          
          <BotToggle
            botName="Futures Bot"
            enabled={botsStatus?.futuresBot?.enabled}
            onToggle={() => toggleBot('futures')}
          />
        </div>
      </div>
      
      {/* Stats Cards por Bot */}
      <div className="bots-stats-section">
        <h3>Bots Performance</h3>
        
        {/* Grid Bot */}
        <div className="bot-section">
          <div className="bot-section-header">
            <h4>Grid Bot (BTC-USDT Spot)</h4>
            <button 
              className="btn-details"
              onClick={() => navigate('/gridbot')}
            >
              View Details →
            </button>
          </div>
          <div className="stats-grid">
            <StatCard
              label="Equity"
              value={`$${summary?.gridBot?.equity?.toLocaleString() || '0.00'}`}
              icon="💰"
              trend={summary?.gridBot?.pnl >= 0 ? 'up' : 'down'}
            />
            <StatCard
              label="Total PnL"
              value={`${summary?.gridBot?.pnl >= 0 ? '+' : ''}${summary?.gridBot?.pnl?.toFixed(2) || '0.00'}%`}
              icon="📈"
              trend={summary?.gridBot?.pnl >= 0 ? 'up' : 'down'}
            />
            <StatCard
              label="Total Trades"
              value={summary?.gridBot?.totalTrades || 0}
              icon="🔄"
            />
            <StatCard
              label="Status"
              value={botsStatus?.gridBot?.enabled ? 'Running' : 'Stopped'}
              icon={botsStatus?.gridBot?.enabled ? '✅' : '⏸️'}
            />
          </div>
        </div>
        
        {/* Solana Sniper */}
        <div className="bot-section">
          <div className="bot-section-header">
            <h4>Solana Sniper (Whale Tracker)</h4>
            <button 
              className="btn-details"
              onClick={() => navigate('/sniper')}
            >
              View Details →
            </button>
          </div>
          <div className="stats-grid">
            <StatCard
              label="Equity"
              value={`$${summary?.sniper?.equity?.toLocaleString() || '0.00'}`}
              icon="🪙"
              trend={summary?.sniper?.pnl >= 0 ? 'up' : 'down'}
            />
            <StatCard
              label="Total PnL"
              value={`${summary?.sniper?.pnl >= 0 ? '+' : ''}${summary?.sniper?.pnl?.toFixed(2) || '0.00'}%`}
              icon="📊"
              trend={summary?.sniper?.pnl >= 0 ? 'up' : 'down'}
            />
            <StatCard
              label="Total Trades"
              value={summary?.sniper?.totalTrades || 0}
              icon="🎯"
            />
            <StatCard
              label="Status"
              value={botsStatus?.sniperBot?.enabled ? 'Running' : 'Stopped'}
              icon={botsStatus?.sniperBot?.enabled ? '✅' : '⏸️'}
            />
          </div>
        </div>
        
        {/* Futures */}
        <div className="bot-section">
          <div className="bot-section-header">
            <h4>Futures Bot (TradingView Signals)</h4>
            <button 
              className="btn-details"
              onClick={() => navigate('/futures')}
            >
              View Details →
            </button>
          </div>
          <div className="stats-grid">
            <StatCard
              label="Equity"
              value={`$${summary?.futures?.equity?.toLocaleString() || '0.00'}`}
              icon="⚡"
              trend={summary?.futures?.pnl >= 0 ? 'up' : 'down'}
            />
            <StatCard
              label="Total PnL"
              value={`${summary?.futures?.pnl >= 0 ? '+' : ''}${summary?.futures?.pnl?.toFixed(2) || '0.00'}%`}
              icon="💹"
              trend={summary?.futures?.pnl >= 0 ? 'up' : 'down'}
            />
            <StatCard
              label="Total Trades"
              value={summary?.futures?.totalTrades || 0}
              icon="📉"
            />
            <StatCard
              label="Status"
              value={botsStatus?.futuresBot?.enabled ? 'Active' : 'Inactive'}
              icon={botsStatus?.futuresBot?.enabled ? '✅' : '⏸️'}
              detail={botsStatus?.futuresBot?.enabled ? 'Listening to signals' : 'Not listening'}
            />
          </div>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h3>Quick Actions</h3>
        <div className="quick-actions-grid">
          <button 
            className="quick-action-btn"
            onClick={() => navigate('/wallets')}
          >
            <span className="icon">👛</span>
            <span className="label">View Wallets</span>
          </button>
          
          <button 
            className="quick-action-btn"
            onClick={() => navigate('/gridbot/config')}
          >
            <span className="icon">⚙️</span>
            <span className="label">Configure Grid Bot</span>
          </button>
          
          <button 
            className="quick-action-btn"
            onClick={() => navigate('/sniper/config')}
          >
            <span className="icon">🔧</span>
            <span className="label">Configure Sniper</span>
          </button>
          
          <button 
            className="quick-action-btn"
            onClick={() => navigate('/futures/config')}
          >
            <span className="icon">🛠️</span>
            <span className="label">Configure Futures</span>
          </button>
        </div>
      </div>
    </div>
  );
}
